import { createStore } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { logger } from '@loc/helpers/src/logger/client'

import type {
  BaseUIState,
  CreatePageStoreOptions,
  PageStoreState,
} from './types'

/**
 * 页面级 Store 工厂函数
 *
 * 创建包含 form / prefs / ui 三层数据的 zustand store：
 * - form + prefs 通过 persist middleware 持久化到 localStorage
 * - ui 仅存在于内存，页面刷新后自动重置
 * - 使用 immer 支持嵌套对象的直接 mutation 写法
 * - 支持 devtools 调试
 *
 * @example
 * ```ts
 * const createCoverStore = () =>
 *   createPageStore<CoverFormValues, CoverPrefs, CoverUI>({
 *     name: 'CoverStore',
 *     persistKey: 'cover-generator-form',
 *     formDefaults: { mode: 'smart', ... },
 *     prefsDefaults: { advancedExpanded: false },
 *     uiDefaults: { isSubmitting: false, isUploading: false, isDetecting: false },
 *   })
 * ```
 */
export function createPageStore<
  TForm extends Record<string, any>,
  TPrefs extends Record<string, any> = Record<string, never>,
  TUI extends BaseUIState = BaseUIState,
>(options: CreatePageStoreOptions<TForm, TPrefs, TUI>) {
  const {
    name,
    persistKey,
    formDefaults,
    prefsDefaults,
    uiDefaults,
    forcedFormDefaults,
    sanitizeOnLoad,
    pickPersistedFormFields,
  } = options

  const mergedFormDefaults = { ...formDefaults, ...forcedFormDefaults } as TForm

  const store = createStore<PageStoreState<TForm, TPrefs, TUI>>()(
    devtools(
      persist(
        immer((set) => ({
          // ─── 数据层 ───
          form: mergedFormDefaults,
          prefs: prefsDefaults,
          ui: uiDefaults,
          _hasHydrated: false,

          // ─── Form Actions ───
          setFormValues: (partial) =>
            set(
              (state) => {
                Object.assign(state.form, partial)
                if (forcedFormDefaults) {
                  Object.assign(state.form, forcedFormDefaults)
                }
              },
              false,
              'setFormValues',
            ),

          setFormField: (key, value) =>
            set(
              (state) => {
                ;(state.form as any)[key] = value
                if (forcedFormDefaults && key in forcedFormDefaults) {
                  ;(state.form as any)[key] = (forcedFormDefaults as any)[key]
                }
              },
              false,
              `setFormField/${String(key)}`,
            ),

          updateForm: (updater) =>
            set(
              (state) => {
                updater(state.form as TForm)
                if (forcedFormDefaults) {
                  Object.assign(state.form, forcedFormDefaults)
                }
              },
              false,
              'updateForm',
            ),

          resetForm: () =>
            set(
              (state) => {
                state.form = mergedFormDefaults as any
              },
              false,
              'resetForm',
            ),

          // ─── Prefs Actions ───
          setPrefs: (partial) =>
            set(
              (state) => {
                Object.assign(state.prefs, partial)
              },
              false,
              'setPrefs',
            ),

          // ─── UI Actions ───
          setUI: (partial) =>
            set(
              (state) => {
                Object.assign(state.ui, partial)
              },
              false,
              'setUI',
            ),
        })),
        {
          name: persistKey,
          storage: createJSONStorage(() => localStorage),
          // 只持久化 form + prefs，ui 不持久化
          partialize: (state) => ({
            form: pickPersistedFormFields
              ? pickPersistedFormFields(state.form)
              : state.form,
            prefs: state.prefs,
          }),
          // 从 localStorage 恢复时校验数据
          onRehydrateStorage: () => (state, error) => {
            if (error || !state) {
              logger.warn(`[${name}] persist rehydrate failed`, error)
              if (state) state._hasHydrated = true
              return
            }
            if (sanitizeOnLoad) {
              state.form = sanitizeOnLoad(state.form) as any
            }
            if (forcedFormDefaults) {
              Object.assign(state.form, forcedFormDefaults)
            }
            state._hasHydrated = true
          },
        },
      ),
      { name },
    ),
  )

  // 开发环境：挂载到 window 并打印状态变更日志
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const win = window as any
    win.__STORES__ = win.__STORES__ || {}
    win.__STORES__[name] = store

    store.subscribe((state, prevState) => {
      logger.dev.log(`[${name}] state updated`, state)
    })
  }

  return store
}
